import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, FileDown, Printer, RefreshCw, Wallet, X } from 'lucide-react';
import { brandingAPI, cashDrawerAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import usePdfDownload from '../../hooks/usePdfDownload';
import { clinicNow, formatClinicDate, formatClinicTime } from '../../utils/clinicTime';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import { PAYMENT_MODE_LABEL } from '../../constants/payments';
import { formatRs, toPaisa } from '../../utils/money';
import CloseDrawerDialog from './CloseDrawerDialog';
import TablePager, { usePagedRows } from '../TablePager';
import { DRAWER_KIND_LABEL, DrawerSlip, drawerTime, overShortLabel, printDrawerSlip, saveDrawerSlipPdf, signedAmount } from './DrawerSlip';

/**
 * Every cashier's drawer, for whoever checks the takings: what each drawer held, what was counted,
 * and whether it came out over or short. A drawer somebody left open is listed whatever day it was
 * opened, and can be counted and closed from here.
 */

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7days', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
];

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Anchored on the clinic's day, never the browser's.
const rangeFor = (preset) => {
  const today = clinicNow();
  today.setHours(0, 0, 0, 0);
  const from = new Date(today);
  let to = today;
  if (preset === 'yesterday') {
    from.setDate(from.getDate() - 1);
    to = new Date(from);
  } else if (preset === '7days') {
    from.setDate(from.getDate() - 6);
  } else if (preset === 'month') {
    from.setDate(1);
  }
  return { from: toIso(from), to: toIso(to) };
};

/** Where a drawer stands. */
export const DrawerStatusChip = ({ drawer }) => {
  let tone = 'bg-sky-100 text-sky-800';
  let text = 'Open';
  if (drawer.status === 'OPEN' && drawer.counted) {
    tone = 'bg-amber-100 text-amber-900';
    text = 'Counted, not closed';
  } else if (drawer.status === 'CLOSED') {
    const paisa = toPaisa(drawer.overShort);
    tone = paisa === 0 ? 'bg-emerald-100 text-emerald-800' : paisa < 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-900';
    text = overShortLabel(drawer.overShort);
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${tone}`}>
      {text}
    </span>
  );
};

const Stat = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="bg-white rounded-xl shadow-md p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
  </div>
);

// Date over time, the way the invoice list shows when a bill was raised.
const DateOverTime = ({ value }) => (
  <>
    <span className="block text-gray-900">{formatClinicDate(value, { day: 'numeric', month: 'short', year: 'numeric' }, '')}</span>
    <span className="block text-xs text-gray-500">{formatClinicTime(value, { hour: 'numeric', minute: '2-digit', hour12: true }, '')}</span>
  </>
);

const Figure = ({ label, value, strong = false }) => (
  <div className="rounded-xl bg-gray-50 px-3 py-2">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className={`tabular-nums ${strong ? 'text-lg font-bold text-gray-900' : 'text-sm font-semibold text-gray-800'}`}>{value}</dd>
  </div>
);

/** One drawer: its figures, every movement in it, and Print slip or Count and close. */
const DrawerDetailModal = ({ result, mayClose, onClose, onCountAndClose }) => {
  const [branding, setBranding] = useState(null);
  const [printError, setPrintError] = useState(null);
  const slipRef = useRef(null);
  const dialogRef = useDialog(onClose, { confirmDiscard: false });

  useEffect(() => {
    let live = true;
    brandingAPI.get().then((b) => { if (live) setBranding(b); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const { drawer, summary = {}, movements = [] } = result;
  const closed = drawer.status === 'CLOSED';
  // An open drawer not yet counted shows what it should hold right now.
  const expected = closed || drawer.counted ? drawer.expectedCash : summary.expectedCash;
  const counted = drawer.countedCash !== null && drawer.countedCash !== undefined;
  const others = summary.otherMethods || [];
  const movementsPaging = usePagedRows(movements, { id: 'cash-drawer-movements', resetOn: [drawer.id] });

  const print = () => {
    setPrintError(null);
    if (!printDrawerSlip(slipRef.current)) {
      setPrintError('Could not open the print window. Please allow pop-ups for this site.');
    }
  };
  const [downloading, download] = usePdfDownload();
  const savePdf = () => download(() => saveDrawerSlipPdf(slipRef.current, drawer));

  return (
    <div
      ref={dialogRef}
      aria-labelledby="drawer-detail-title"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Wallet className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id="drawer-detail-title" className="text-lg font-bold text-white truncate">{`${drawer.cashierName || 'Cashier'}'s drawer`}</h2>
              <p className="text-emerald-100 text-sm truncate">
                {`Opened ${drawerTime(drawer.openedAt)}${closed ? `, closed ${drawerTime(drawer.closedAt)}` : ''}`}
              </p>
            </div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <DrawerStatusChip drawer={drawer} />
            {closed && drawer.closedByName && <span className="text-xs text-gray-500">{`Closed by ${drawer.closedByName}`}</span>}
          </div>

          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Figure label="Starting cash" value={formatRs(summary.openingFloat ?? drawer.openingFloat)} />
            <Figure label="Cash in" value={formatRs(summary.cashIn)} />
            <Figure label="Cash out" value={formatRs(summary.cashOut)} />
            <Figure label={closed ? 'Expected cash' : 'Should hold now'} value={formatRs(expected)} strong />
            <Figure label="Counted" value={counted ? formatRs(drawer.countedCash) : 'Not counted yet'} />
            <Figure label="Difference" value={counted ? overShortLabel(drawer.overShort) : '—'} />
          </dl>

          {drawer.differenceReason && (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span className="font-semibold">Reason: </span>{drawer.differenceReason}
            </p>
          )}

          {others.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Taken by other methods, not in the drawer</h3>
              <div className="flex flex-wrap gap-2">
                {others.map((m) => (
                  <span key={m.key} className="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-700 tabular-nums">
                    {`${PAYMENT_MODE_LABEL[m.key] || m.key}: ${formatRs(m.amount)} (${m.count})`}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{`Everything that went through the drawer (${movements.length})`}</h3>
            {movements.length === 0 ? (
              <p className="text-sm text-gray-500">Nothing has gone in or out yet.</p>
            ) : (
              <>
              <TablePager paging={movementsPaging} noun="movement" className="px-3 !rounded-lg" />
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-[14%]" />
                  <col className="w-[40%]" />
                  <col className="w-[16%]" />
                  <col className="w-[14%]" />
                  <col className="w-[16%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="py-2 pr-2">Time</th>
                    <th className="py-2 pr-2">What</th>
                    <th className="py-2 pr-2">Method</th>
                    <th className="py-2 pr-2 text-right">Amount</th>
                    <th className="py-2 pl-2">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {movementsPaging.pageRows.map((m) => (
                    <tr key={m.id}>
                      <td className="py-2 pr-2 text-gray-600 whitespace-nowrap">{formatClinicTime(m.createdAt, { hour: 'numeric', minute: '2-digit', hour12: true }, '')}</td>
                      <td className="py-2 pr-2">
                        <span className="block font-medium text-gray-900">{DRAWER_KIND_LABEL[m.kind] || m.kind}</span>
                        <span className="block truncate text-xs text-gray-500">{m.description}</span>
                      </td>
                      <td className="py-2 pr-2 text-gray-700">{PAYMENT_MODE_LABEL[m.paymentMode] || m.paymentMode}</td>
                      <td className={`py-2 pr-2 text-right font-semibold tabular-nums ${toPaisa(m.amount) < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                        {signedAmount(m.amount)}
                      </td>
                      <td className="py-2 pl-2 truncate text-gray-600">{m.performedByName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePager paging={movementsPaging} noun="movement" position="bottom" className="px-3 !rounded-lg" />
              </>
            )}
          </div>

          {printError && <p role="alert" className="text-sm text-red-600">{printError}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap justify-end gap-2">
          {closed && (
            <button type="button" onClick={savePdf} disabled={downloading} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50">
              <FileDown className="h-4 w-4" aria-hidden="true" /> {downloading ? 'Making the PDF…' : 'Download PDF'}
            </button>
          )}
          {closed && (
            <button type="button" onClick={print} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
              <Printer className="h-4 w-4" aria-hidden="true" /> Print slip
            </button>
          )}
          {!closed && mayClose && (
            <button
              type="button"
              onClick={() => onCountAndClose({ ...drawer, expectedCash: expected })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
            >
              <Wallet className="h-4 w-4" aria-hidden="true" /> Count and close
            </button>
          )}
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
            Close
          </button>
        </div>

        {closed && (
          <div className="hidden" aria-hidden="true">
            <div ref={slipRef}>
              <DrawerSlip result={result} branding={branding} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CashDrawersView = ({ hasPermission }) => {
  const showToast = useToast();
  const mayClose = typeof hasPermission === 'function' ? hasPermission('CASH_DRAWERS', 'CLOSE_ANY_DRAWER') : true;
  const [preset, setPreset] = useState('today');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [closing, setClosing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cashDrawerAPI.list(rangeFor(preset));
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Could not load the cash drawers');
    } finally {
      setLoading(false);
    }
  }, [preset]);

  useEffect(() => { load(); }, [load]);

  // The figures above the list, from the same rows the list shows.
  const stats = useMemo(() => {
    let open = 0;
    let closed = 0;
    let short = 0;
    let over = 0;
    rows.forEach((r) => {
      if (r.status === 'OPEN') {
        open += 1;
        return;
      }
      closed += 1;
      const paisa = toPaisa(r.overShort);
      if (paisa < 0) short -= paisa;
      if (paisa > 0) over += paisa;
    });
    return { open, closed, short: short / 100, over: over / 100 };
  }, [rows]);

  const openDetail = async (row) => {
    try {
      setDetail(await cashDrawerAPI.getById(row.id));
    } catch (err) {
      showToast('error', err?.message || 'Could not open this drawer');
    }
  };

  const th = 'px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500';
  const paging = usePagedRows(rows, { id: 'cash-drawers', resetOn: [preset] });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Open now" value={stats.open} />
        <Stat label="Closed" value={stats.closed} />
        <Stat label="Short" value={formatRs(stats.short)} tone={stats.short > 0 ? 'text-red-700' : 'text-gray-900'} />
        <Stat label="Over" value={formatRs(stats.over)} tone={stats.over > 0 ? 'text-amber-700' : 'text-gray-900'} />
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-5 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Dates">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={preset === p.key}
              onClick={() => setPreset(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                preset === p.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="flex-1 min-w-[12rem] text-xs text-gray-500">A drawer still open is listed whatever day it was opened.</p>
        <button type="button" onClick={load} aria-label="Refresh" className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div role="alert" className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-center gap-2 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl">
        <TablePager paging={paging} noun="drawer" className="px-4" />
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-[18%]" />
            <col className="w-[17%]" />
            <col className="w-[17%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[15%]" />
          </colgroup>
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className={`${th} text-left`}>Cashier</th>
              <th className={`${th} text-left`}>Opened</th>
              <th className={`${th} text-left`}>Closed</th>
              <th className={`${th} text-right`}>Starting cash</th>
              <th className={`${th} text-right`}>Expected</th>
              <th className={`${th} text-right`}>Counted</th>
              <th className={`${th} text-left`}>Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">No drawers were opened on these dates.</td></tr>
            ) : paging.pageRows.map((r) => (
              <tr
                key={r.id}
                tabIndex={0}
                onClick={() => openDetail(r)}
                onKeyDown={onActivateKey(() => openDetail(r))}
                className={`cursor-pointer hover:bg-gray-50 transition-colors ${ROW_FOCUS_RING}`}
              >
                <td className="px-4 py-3 text-sm font-semibold text-gray-900 truncate">{r.cashierName}</td>
                <td className="px-4 py-3 text-sm text-gray-600"><DateOverTime value={r.openedAt} /></td>
                <td className="px-4 py-3 text-sm text-gray-600">{r.closedAt ? <DateOverTime value={r.closedAt} /> : '—'}</td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">{formatRs(r.openingFloat)}</td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">{r.expectedCash != null ? formatRs(r.expectedCash) : '—'}</td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">{r.countedCash != null ? formatRs(r.countedCash) : '—'}</td>
                <td className="px-4 py-3"><DrawerStatusChip drawer={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <TablePager paging={paging} noun="drawer" position="bottom" className="px-4" />
      </div>

      {detail && (
        <DrawerDetailModal
          result={detail}
          mayClose={mayClose}
          onClose={() => setDetail(null)}
          onCountAndClose={(drawer) => setClosing(drawer)}
        />
      )}

      {closing && (
        <CloseDrawerDialog
          drawer={closing}
          supervisor
          onClosed={(res) => {
            setDetail((current) => (current?.drawer?.id === res?.drawer?.id ? res : current));
            load();
          }}
          onClose={() => setClosing(null)}
        />
      )}
    </div>
  );
};

export default CashDrawersView;
